// StepTeam — step 2 of the wizard. Lets the user assign named people to
// teams (Business / Engineering / Security / Compliance) and add general
// project members.
//
// UI-ONLY: nothing on this step is sent to the backend. It reads/writes
// form.teamAssignments and form.projectMembers, which persist in the local
// draft but are deliberately omitted from the /intake payload (see the
// "UI-ONLY" markers in ../types.ts). Purely organizational bookkeeping for
// the user's own reference for now.
import type { ComponentType, KeyboardEvent } from 'react'
import { useState } from 'react'
import { Users, Briefcase, Code2, ShieldCheck, FileText, Search } from 'lucide-react'

import type { NewProjectForm, UpdateForm } from '../types'

const TEAMS: Array<{
  key: string
  title: string
  tag: 'mandatory' | 'required' | 'optional'
  placeholder: string
  Icon: ComponentType<{ size?: number }>
}> = [
  { key: 'business', title: 'Business Team', tag: 'mandatory', placeholder: 'Select Business Lead…', Icon: Briefcase },
  { key: 'engineering', title: 'Engineering Team', tag: 'mandatory', placeholder: 'Assign Engineering Unit…', Icon: Code2 },
  { key: 'security', title: 'Security Team', tag: 'required', placeholder: 'Find Security Reviewer…', Icon: ShieldCheck },
  { key: 'compliance', title: 'Compliance Team', tag: 'optional', placeholder: 'Add Legal Representative…', Icon: FileText },
]

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

function AssigneeChip({ name, onRemove }: { name: string; onRemove: () => void }) {
  return (
    <span className="assignee-chip">
      <span className="avatar-mini" style={{ background: 'linear-gradient(135deg, var(--x-amber), var(--x-orange))' }}>
        {initials(name)}
      </span>
      {name}
      <span className="x" onClick={onRemove}>×</span>
    </span>
  )
}

export function StepTeam({
  form,
  update,
}: {
  form: NewProjectForm
  update: UpdateForm
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [memberDraft, setMemberDraft] = useState('')

  const addAssignee = (teamKey: string) => {
    const value = (drafts[teamKey] ?? '').trim()
    if (!value) return
    const current = form.teamAssignments[teamKey] ?? []
    if (current.includes(value)) {
      setDrafts((d) => ({ ...d, [teamKey]: '' }))
      return
    }
    update({ teamAssignments: { ...form.teamAssignments, [teamKey]: [...current, value] } })
    setDrafts((d) => ({ ...d, [teamKey]: '' }))
  }

  const removeAssignee = (teamKey: string, name: string) => {
    const current = form.teamAssignments[teamKey] ?? []
    update({ teamAssignments: { ...form.teamAssignments, [teamKey]: current.filter((n) => n !== name) } })
  }

  const addMember = () => {
    const value = memberDraft.trim()
    if (!value || form.projectMembers.includes(value)) {
      setMemberDraft('')
      return
    }
    update({ projectMembers: [...form.projectMembers, value] })
    setMemberDraft('')
  }

  const onTeamKey = (teamKey: string) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addAssignee(teamKey)
    }
  }

  return (
    <>
      <header className="step-header">
        <div className="step-section-tag">
          <Users size={14} />
          Assigned teams
        </div>
        <h1 className="step-h1">Who is building this?</h1>
        <p className="step-lede">
          Define the core operational structure by assigning specialized units. Each team receives
          scoping modules based on their functional domain.
        </p>
      </header>

      <div className="team-grid">
        {TEAMS.map(({ key, title, tag, placeholder, Icon }) => {
          const assignees = form.teamAssignments[key] ?? []
          return (
            <div key={key} className="team-card">
              <div className="team-head">
                <div className="team-name">
                  <div className="team-icon"><Icon size={14} /></div>
                  <span className="team-title">{title}</span>
                </div>
                <span className={`team-tag ${tag}`}>{tag}</span>
              </div>
              <div className="team-search">
                <Search size={14} />
                <input
                  type="text"
                  placeholder={placeholder}
                  value={drafts[key] ?? ''}
                  onChange={(e) => setDrafts((d) => ({ ...d, [key]: e.target.value }))}
                  onKeyDown={onTeamKey(key)}
                  onBlur={() => addAssignee(key)}
                />
              </div>
              <div className="team-assigned">
                {assignees.map((name) => (
                  <AssigneeChip key={name} name={name} onRemove={() => removeAssignee(key, name)} />
                ))}
              </div>
            </div>
          )
        })}
      </div>

      <section className="members-card">
        <div className="members-title">Project Members</div>
        <p className="members-desc">
          Add general stakeholders and observers who need visibility into the generation process.
        </p>
        <div className="members-input">
          {form.projectMembers.map((m) => (
            <AssigneeChip
              key={m}
              name={m}
              onRemove={() => update({ projectMembers: form.projectMembers.filter((x) => x !== m) })}
            />
          ))}
          <input
            type="text"
            placeholder="Type name or email…"
            value={memberDraft}
            onChange={(e) => setMemberDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addMember()
              }
            }}
            onBlur={addMember}
          />
        </div>
        <div className="members-foot">
          <span className="members-count">{form.projectMembers.length} member(s) added</span>
        </div>
      </section>
    </>
  )
}
